<template>
    <div>
        <v-container fluid>
            <v-card class="pb-1 mb-3">
                <v-card-title>{{ title }}</v-card-title>
                <v-card-text>
                <v-row justify="center" align="center" v-if="loading">
                    <div class="text-center">
                    <div>
                        <v-progress-circular
                        class="my-4"
                        :size="50"
                        width="5"
                        color="primary"
                        indeterminate
                        ></v-progress-circular>
                    </div>
                    <p class="display-1">{{ $t("Loading") }}...</p>
                    </div>
                </v-row>
                <v-row v-if="!loading">
                    <v-col cols="5" sm="5">
                        <v-text-field
                        v-model="dataManagementJob.description"
                        :label="$t('Description')"
                        :error-messages="descriptionErrors"
                        @input="$v.dataManagementJob.description.$touch()"
                        @blur="$v.dataManagementJob.description.$touch()"
                        ></v-text-field>
                    </v-col>
                        <v-col
                        cols="7"
                        class="text-right"
                        align-self="center"
                        >
                            <v-row no-gutters justify="end">
                                <v-btn @click="close">
                                {{ $t("Cancel") }}
                                </v-btn>
                                <v-btn
                                color="primary"
                                @click.prevent="save"
                                :loading="saving"
                                >
                                {{ $t("Save") }}
                                </v-btn>
                            </v-row>
                        </v-col>
                </v-row>
                </v-card-text>
            </v-card>
            <v-card class="pb-1 mb-1">
                <v-card-text>
                    <v-row align="center">
                        <v-col cols="2" sm="3">
                            <v-select
                                v-model="dataManagementJob.type"
                                :items="jobTypes"
                                :label="$t('Action')"
                                item-text="text"
                                item-value="id"
                                :error-messages="typeErrors"
                                @change="$v.dataManagementJob.type.$touch()"
                                @blur="$v.dataManagementJob.type.$touch()"
                            ></v-select>
                        </v-col>
                        <v-col cols="8">
                            <v-text-field
                                readonly
                                v-model="dataManagementJob.displayConditionStr"
                                :label="$t('Condition')"
                                :append-icon="
                                    dataManagementJob.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'
                                "
                                @click:append="editCondition"
                                :error-messages="conditionErrors"
                                @blur="$v.dataManagementJob.conditionStr.$touch()"
                                @change="$v.dataManagementJob.conditionStr.$touch()"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <div class="mt-5">
                        {{ $t("AffectedDocuments") }}:
                        <v-progress-circular
                            v-if="loading"
                            :size="25"
                            color="primary"
                            indeterminate
                        ></v-progress-circular>
                        <span v-if="!loading">{{ affectedDocuments }}</span>
                    </div>
                </v-card-text>
            </v-card>
            <SearchCriteriaBuilderModal
                :ref="conditionModalKey"
                :contentTypeIDs="userContentTypes"
                @on-condition-saved="onConditionChanged"
                />
        </v-container>
    </div>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { DateTime } from 'luxon';
import SearchCriteriaBuilderModal from '@/components/search/SearchCriteriaBuilderModal.vue';
import DataManagementJobService from '@/services/DataManagementJobService';
import Utils from '@/ecm/common/Utils.js';
import DataManagementJob from '@/ecm/models/DataManagementJob';
import DataManagementJobType from '@/ecm/variables/DataManagementJobType';
import FieldType from '@/ecm/variables/FieldType';
import ConditionItem from '@/ecm/models/search/ConditionItem';
import { VAR_PREFIX } from '../../../ecm/common/Constants';

export default {
    components: {
        SearchCriteriaBuilderModal,
    },
    props: {
        documentId: {
            type: Number,
            default: 0,
        },
    },
    created() {
        this.$v.$reset();
        const jobId = this.$route.params.id;
        if (jobId > 0) {
            DataManagementJobService.getById(jobId).then((job) => {
                this.edit(job);
            });
        } else {
            this.new();
        }
    },
    data() {
        return {
            title: this.$t('DataManagement'),
            jobTypes: [
                {
                    id: DataManagementJobType.ExportWithMetadata,
                    text: this.$t('ExportWithMetadata'),
                },
                {
                    id: DataManagementJobType.ExportOnlyFiles,
                    text: this.$t('ExportOnlyFiles'),
                },
                {
                    id: DataManagementJobType.Delete,
                    text: this.$t('Site.DocumentManagement.Destroy'),
                },
            ],
            dataManagementJob: new DataManagementJob(),
            loading: false,
            affectedDocuments: 0,
            open: false,
            conditionModalKey: 'conditionModalKey',
            searchAllVersions: false,
            isNew: true,
            saving: false
        };
    },
    mixins: [validationMixin],
    validations: {
        dataManagementJob: {
            description: { required },
            type: { required },
            conditionStr: { required },
        },
    },
    computed: {
        descriptionErrors() {
            const errors = [];
            if (!this.$v.dataManagementJob.description.$dirty) return errors;
            !this.$v.dataManagementJob.description.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        conditionErrors() {
            const errors = [];
            if (!this.$v.dataManagementJob.conditionStr.$dirty) return errors;
            !this.$v.dataManagementJob.conditionStr.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        typeErrors() {
            const errors = [];
            if (!this.$v.dataManagementJob.type.$dirty) return errors;
            !this.$v.dataManagementJob.type.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        userContentTypes() {
            return this.$store.getters['account/userContentTypes'].map((x) => x.id);
        },
        conditionModalInstance() {
            return this.$refs[this.conditionModalKey];
        },
    },
    methods: {
        editCondition() {
            this.conditionModalInstance.edit(this.dataManagementJob.conditionStr);
        },
        onConditionChanged(condition) {
            this.dataManagementJob.conditionStr = condition.conditionStr;
            this.dataManagementJob.displayConditionStr = condition.displayConditionStr;
            this.UpdateAffectedDocuments();
        },
        UpdateAffectedDocuments() {
            this.loading = true;
            DataManagementJobService.getDocumentCount(
                this.dataManagementJob.conditionStr
            )
                .then((documentCount) => {
                    this.loading = false;
                    this.affectedDocuments = documentCount;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        save() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.saving = true;

            DataManagementJobService.save(this.dataManagementJob.toDto())
                .then((result) => {
                    if (result) {
                        if (this.isNew) {
                            Utils.toast.success(
                                this.$t('Job') + ' ' + this.$t('CreatedSuccessfully')
                            );
                        } else {
                            Utils.toast.success(
                                this.$t('Job') + ' ' + this.$t('UpdatedSuccessfully')
                            );
                        }
                        this.close();
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.saving = false;
                });
        },
        new() {
            this.dataManagementJob = new DataManagementJob();
            this.affectedDocuments = 0;
            this.$v.$reset();
        },
        edit(dto) {
            this.dataManagementJob = new DataManagementJob(dto);
            this.isNew = false;
            this.getFieldValuesToUserFormat();
            this.UpdateAffectedDocuments();
            this.$v.$reset();
        },
        close() {
            this.$router.push({ path: '/administration/jobs' });
        },
        getFieldValuesToUserFormat() {
            const conditionItems = [];
            const standardCodes = this.$store.getters['account/searchStandardFields'].map(sf => sf.Code);

            this.dataManagementJob.displayConditionStr.split(';').forEach((condition) => {
                var conditionItem = new ConditionItem(condition);

                const isStandardField = standardCodes.some(c => c === conditionItem.fieldCode);

                if (!conditionItem.fieldValue.startsWith(VAR_PREFIX)) {
                    if (conditionItem.fieldType == FieldType.Numeric && !isStandardField) {
                        const userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
                        const userThouSep = this.$store.getters['account/user'].MetricThousandSep;

                        const numberWithThousandSeparator = new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: true
                        }).format(conditionItem.fieldValue);
                        conditionItem.fieldValue = numberWithThousandSeparator.replace(',', userThouSep).replace('.', userDecSep);

                    } else if (conditionItem.fieldType == FieldType.DateTime && !isStandardField) {
                        const userDateFormat = this.$store.getters['account/user'].MetricDateFormat;

                        const inputDate = DateTime.fromFormat(conditionItem.fieldValue, 'dd.MM.yyyy');
                        conditionItem.fieldValue = inputDate.toFormat(userDateFormat);
                    }
                }

                conditionItems.push(conditionItem);
            });

            if (conditionItems.length > 0) {
                this.dataManagementJob.conditionStr = conditionItems
                    .map((item) => item.conditionStr())
                    .join(';');
                this.dataManagementJob.displayConditionStr = conditionItems
                    .map((item) => item.conditionStr())
                    .join(';');
            }

        }
    },
};
</script>
